// Lightweight client-side URL gate used by every link block before
// attaching an href. Defense-in-depth on top of the server-side
// `Ui::Urls::Canonicalize` + host-allowlist already enforced by
// `Ui::Documents::Validate`. The renderer never trusts a URL blindly.
//
// Returns `null` for any URL that fails the scheme or shape check; the
// caller renders the label as plain text in that case.
const ALLOWED_SCHEMES = new Set(["https:", "http:", "mailto:", "tel:"])

export function safeHref(href: unknown): string | null {
  if (typeof href !== "string") return null
  const trimmed = href.trim()
  if (trimmed === "") return null

  // Reject `javascript:`, `data:`, `vbscript:`, `file:`, etc. before
  // letting the URL parser see them.
  const lower = trimmed.toLowerCase()
  if (lower.startsWith("javascript:")) return null
  if (lower.startsWith("data:")) return null
  if (lower.startsWith("vbscript:")) return null
  if (lower.startsWith("file:")) return null

  // Relative paths under a known origin are fine — they hit the same
  // host the operator is on (Valet portal). Same-origin relative links
  // have no scheme, so `new URL()` would throw.
  if (trimmed.startsWith("/")) return trimmed
  if (trimmed.startsWith("#")) return trimmed

  try {
    const u = new URL(trimmed)
    if (!ALLOWED_SCHEMES.has(u.protocol)) return null
    return u.toString()
  } catch {
    return null
  }
}
